import React, { useMemo, useState } from 'react';
import { Table, Input, Select, Tag, Space, Button } from 'antd';
import { ReloadOutlined, HistoryOutlined } from '@ant-design/icons';

const fmtDate = (v: string | null): string => {
    if (!v) return '';
    const d = new Date(v.replace(' ', 'T'));
    if (isNaN(d.getTime())) return v;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toMillis = (v: string | null): number => {
    if (!v) return -1;
    const d = new Date(v.replace(' ', 'T'));
    return isNaN(d.getTime()) ? -1 : d.getTime();
};

interface HistoryRow {
    categoria: string;
    id_usuari: number;
    usuari_nom: string;
    usuari_cognoms: string;
    usuari_email: string;
    aulas: string | null;
    moment: string | null;
    tipus: string | null;
    descripcio: string | null;
    valor: number | null;
    estat: string | null;
}

interface TabHistorialProps {
    isAdmin: boolean;
    isProfesor: boolean;
    loading: boolean;
    history: HistoryRow[];
    teacherAulas: any[];
    selectedAulaIdEquipment: string | number;
    setSelectedAulaIdEquipment: (val: string | number) => void;
    aulaStudentsMap: Record<string | number, Set<number>>;
    loadStudentsForAula: (aulaId: string | number) => Promise<void>;
    fetchHistorial: () => Promise<void>;
}

const CATEGORY_META: Record<string, { label: string; color: string }> = {
    SHOP:       { label: 'Objeto de tienda',   color: 'geekblue' },
    PUNTS:      { label: 'Puntos',             color: 'gold' },
    JAVABALL:   { label: 'Javaball',           color: 'volcano' },
    JAVAMON:    { label: 'Javamon capturado',  color: 'green' },
    ACTIVITAT:  { label: 'Recompensa actividad', color: 'cyan' },
    PILDORA:    { label: 'Recompensa píldora', color: 'purple' },
    JAVAMON_US: { label: 'Javamon usado',      color: 'magenta' },
};

const ESTAT_COLOR: Record<string, string> = {
    CAPTURAT: 'green',
    OBTINGUT: 'green',
    ESCAPADO: 'red',
    ESCAPAT: 'red',
    FALLAT: 'red',
    PENDENT: 'default',
    USAT: 'blue',
};

export const TabHistorial: React.FC<TabHistorialProps> = ({
    isAdmin,
    isProfesor,
    loading,
    history,
    teacherAulas,
    selectedAulaIdEquipment,
    setSelectedAulaIdEquipment,
    aulaStudentsMap,
    loadStudentsForAula,
    fetchHistorial
}) => {
    const [searchUser, setSearchUser] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const filtered = useMemo(() => {
        return (history || []).filter((r: HistoryRow) => {
            const uId = r.id_usuari != null ? Number(r.id_usuari) : null;
            const userAulas: string[] = r.aulas ? String(r.aulas).split(',').map((s) => s.trim()) : [];

            // Filtro por aula seleccionada (mismo patrón que Equipamiento de Usuarios)
            if (selectedAulaIdEquipment !== 'all') {
                const selectedStr = String(selectedAulaIdEquipment);
                if (r.aulas !== undefined && r.aulas !== null) {
                    if (!userAulas.includes(selectedStr)) return false;
                } else {
                    const studentSet = aulaStudentsMap[selectedAulaIdEquipment] || aulaStudentsMap[selectedStr];
                    if (!studentSet || (uId != null && !studentSet.has(uId))) return false;
                }
            } else if (isProfesor) {
                if (r.aulas !== undefined && r.aulas !== null) {
                    const teacherAulaIds = new Set(teacherAulas.map((a: any) => String(a.id)));
                    const inTeacherAula = userAulas.some((aid: string) => teacherAulaIds.has(aid));
                    if (!inTeacherAula) return false;
                } else if (Object.keys(aulaStudentsMap).length > 0) {
                    const inAnyAula = Object.values(aulaStudentsMap).some((set) => uId != null && set.has(uId));
                    if (!inAnyAula) return false;
                }
            }

            // Filtro por categoría
            if (categoryFilter.length > 0 && !categoryFilter.includes(r.categoria)) return false;

            // Filtro por rango de fechas (solo filas con moment). Inputs type=date => YYYY-MM-DD
            if (dateFrom || dateTo) {
                if (!r.moment) return false;
                const m = toMillis(r.moment);
                if (m < 0) return false;
                if (dateFrom) {
                    const from = new Date(dateFrom + 'T00:00:00').getTime();
                    if (m < from) return false;
                }
                if (dateTo) {
                    const to = new Date(dateTo + 'T23:59:59').getTime();
                    if (m > to) return false;
                }
            }

            // Filtro por texto (nombre/email/descripción)
            const needle = searchUser.toLowerCase();
            if (needle) {
                const fullName = `${r.usuari_nom || ''} ${r.usuari_cognoms || ''}`.toLowerCase();
                const email = (r.usuari_email || '').toLowerCase();
                const descr = (r.descripcio || '').toLowerCase();
                const tipus = (r.tipus || '').toLowerCase();
                if (!fullName.includes(needle) && !email.includes(needle)
                    && !descr.includes(needle) && !tipus.includes(needle)) {
                    return false;
                }
            }

            return true;
        });
    }, [history, selectedAulaIdEquipment, aulaStudentsMap, isProfesor, teacherAulas, categoryFilter, dateFrom, dateTo, searchUser]);

    const columns = [
        {
            title: 'Fecha',
            dataIndex: 'moment',
            key: 'moment',
            width: 170,
            render: (v: string | null) => v ? fmtDate(v) : <Tag>sin fecha</Tag>,
            sorter: (a: HistoryRow, b: HistoryRow) => toMillis(a.moment) - toMillis(b.moment),
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Alumno',
            key: 'alumno',
            render: (_: any, r: HistoryRow) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{`${r.usuari_nom || ''} ${r.usuari_cognoms || ''}`.trim() || `#${r.id_usuari}`}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{r.usuari_email}</div>
                </div>
            ),
        },
        {
            title: 'Categoría',
            dataIndex: 'categoria',
            key: 'categoria',
            width: 180,
            render: (v: string) => {
                const meta = CATEGORY_META[v] || { label: v, color: 'default' };
                return <Tag color={meta.color}>{meta.label}</Tag>;
            },
        },
        {
            title: 'Detalle',
            dataIndex: 'descripcio',
            key: 'descripcio',
            render: (v: string | null, r: HistoryRow) => (
                <div>
                    <div>{v}</div>
                    {r.tipus && <div style={{ fontSize: 12, color: '#999' }}>{r.tipus}</div>}
                </div>
            ),
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            key: 'valor',
            width: 90,
            align: 'right' as const,
            render: (v: number | null) => (v != null ? v : '—'),
        },
        {
            title: 'Estado',
            dataIndex: 'estat',
            key: 'estat',
            width: 120,
            render: (v: string | null) => v ? <Tag color={ESTAT_COLOR[v] || 'default'}>{v}</Tag> : '—',
        },
    ];

    return (
        <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <HistoryOutlined /> Historial de recompensas
            </h2>
            <Space style={{ marginBottom: 16 }} wrap>
                {teacherAulas.length > 0 && (
                    <Select
                        value={selectedAulaIdEquipment}
                        onChange={(val) => {
                            setSelectedAulaIdEquipment(val);
                            loadStudentsForAula(val);
                        }}
                        style={{ width: 240 }}
                        placeholder="Seleccionar aula"
                        showSearch
                        optionFilterProp="children"
                    >
                        <Select.Option value="all">
                            🏫 {isProfesor ? `Todas mis aulas (${teacherAulas.length})` : `Todas las aulas (${teacherAulas.length})`}
                        </Select.Option>
                        {teacherAulas.map((aula: any) => (
                            <Select.Option key={aula.id} value={aula.id}>
                                🏫 {aula.nom || `Aula ${aula.id}`}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                <Select
                    mode="multiple"
                    allowClear
                    placeholder="Filtrar por categoría"
                    style={{ minWidth: 260 }}
                    value={categoryFilter}
                    onChange={(vals) => setCategoryFilter(vals)}
                    options={Object.keys(CATEGORY_META).map((k) => ({ value: k, label: CATEGORY_META[k].label }))}
                />
                <Space.Compact>
                    <Input style={{ width: 55, pointerEvents: 'none', background: '#fafafa' }} value="Desde" readOnly />
                    <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        style={{ width: 150 }}
                    />
                </Space.Compact>
                <Space.Compact>
                    <Input style={{ width: 55, pointerEvents: 'none', background: '#fafafa' }} value="Hasta" readOnly />
                    <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        style={{ width: 150 }}
                    />
                </Space.Compact>
                <Input
                    placeholder="Buscar alumno, detalle o tipo..."
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    style={{ width: 320 }}
                    allowClear
                />
                <Button icon={<ReloadOutlined />} onClick={() => fetchHistorial()}>
                    Recargar
                </Button>
            </Space>

            <Table
                rowKey={(r: HistoryRow, idx) => `${r.categoria}-${r.id_usuari}-${r.moment || 'nd'}-${idx}`}
                loading={loading}
                columns={columns}
                dataSource={filtered}
                size="small"
                pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} eventos` }}
            />
        </div>
    );
};

export default TabHistorial;
